import React, { useState } from 'react';
import { FiBarChart2, FiTrendingUp, FiShield } from 'react-icons/fi';

const CompetitiveTab = ({ insightData, Section }) => {
  const [competitiveExpanded, setCompetitiveExpanded] = useState(true);
  const onToggleCompetitive = React.useCallback(() => setCompetitiveExpanded(v => !v), []);

  if (!insightData?.heptapod_dimensional_analysis?.competitive_intelligence) return null;

  const competitiveIntel = insightData.heptapod_dimensional_analysis.competitive_intelligence;

  return (
    <>
      {/* Competitive Intelligence */}
      <Section title="Competitive Intelligence" iconComponent={FiBarChart2} collapsible expanded={competitiveExpanded} onToggle={onToggleCompetitive}>
        {competitiveExpanded && (
          <div className="space-y-4">
            {/* Position Assessment */}
            {competitiveIntel.position_assessment && (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 transition-all duration-200">
                <h4 className="text-sm font-semibold text-gray-700 mb-4">Position Assessment</h4>
                
                {/* Feature Differentiation */}
                {competitiveIntel.position_assessment.feature_differentiation && competitiveIntel.position_assessment.feature_differentiation.length > 0 && (
                  <div className="mb-6">
                    <h5 className="text-sm font-semibold text-gray-600 mb-3">Feature Differentiation</h5>
                    <div className="space-y-3">
                      {competitiveIntel.position_assessment.feature_differentiation.map((feature, idx) => (
                        <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-center mb-2">
                            <div className="font-semibold text-gray-700 text-sm">
                              {feature.capability}
                            </div>
                            <span className={`px-2 py-1 rounded text-xs font-semibold capitalize ${
                              feature.our_strength === 'superior' ? 'bg-green-100 text-green-800' : 
                              feature.our_strength === 'equal' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {feature.our_strength}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500">
                            {feature.competitor_comparison}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Market Position */}
                {(competitiveIntel.position_assessment.pricing_position || competitiveIntel.position_assessment.relationship_moats?.switching_costs) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {competitiveIntel.position_assessment.pricing_position && (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                        <div className="text-xs text-gray-700 font-semibold mb-1">PRICING POSITION</div>
                        <div className="text-sm text-gray-800 font-bold">
                          {competitiveIntel.position_assessment.pricing_position}
                        </div>
                      </div>
                    )}
                    
                    {competitiveIntel.position_assessment.relationship_moats?.switching_costs && (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                        <div className="text-xs text-gray-700 font-semibold mb-1">SWITCHING COSTS</div>
                        <div className="text-sm text-gray-800 font-bold">
                          {competitiveIntel.position_assessment.relationship_moats.switching_costs}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Threat Analysis */}
            {competitiveIntel.threat_analysis && (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 transition-all duration-200">
                <h4 className="text-sm font-semibold text-gray-700 mb-4">Threat Analysis</h4>
                
                {/* Active Evaluations */}
                {competitiveIntel.threat_analysis.active_evaluations && competitiveIntel.threat_analysis.active_evaluations.length > 0 && (
                  <div className="mb-6">
                    <h5 className="text-sm font-semibold text-gray-600 mb-3">Active Evaluations</h5>
                    <div className="space-y-3">
                      {competitiveIntel.threat_analysis.active_evaluations.map((evaluation, idx) => (
                        <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-center mb-2">
                            <div className="font-semibold text-gray-700 text-sm">
                              {evaluation.competitor}
                            </div>
                            <span className={`px-2 py-1 rounded text-xs font-semibold capitalize ${
                              evaluation.threat_level === 'high' ? 'bg-red-100 text-red-800' : 
                              evaluation.threat_level === 'medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                            }`}>
                              {evaluation.threat_level} Threat
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mb-2">
                            Stage: {evaluation.evaluation_stage}
                          </div>
                          <div className="text-xs text-gray-600">
                            <strong>Mitigation:</strong> {evaluation.mitigation_strategy}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Market Dynamics */}
                {competitiveIntel.threat_analysis.market_dynamics && competitiveIntel.threat_analysis.market_dynamics.length > 0 && (
                  <div>
                    <h5 className="text-sm font-semibold text-gray-600 mb-3">Market Dynamics</h5>
                    <div className="space-y-2">
                      {competitiveIntel.threat_analysis.market_dynamics.map((dynamic, idx) => (
                        <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-700">
                          {dynamic}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Differentiation Opportunities */}
            {competitiveIntel.differentiation_opportunities && competitiveIntel.differentiation_opportunities.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 transition-all duration-200">
                <h4 className="text-sm font-semibold text-gray-700 mb-4">Differentiation Opportunities</h4>
                <div className="space-y-3">
                  {competitiveIntel.differentiation_opportunities.map((opportunity, idx) => (
                    <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="font-semibold text-gray-800 text-sm mb-2">
                        {opportunity.opportunity}
                      </div>
                      <div className="text-xs text-gray-600 mb-2">
                        <strong>Advantage:</strong> {opportunity.competitive_advantage}
                      </div>
                      {opportunity.implementation_requirements && (
                        <div className="text-xs text-gray-500">
                          <strong>Requirements:</strong> {opportunity.implementation_requirements.join(', ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Section>
    </>
  );
};

export default CompetitiveTab;
